import axios, { AxiosError } from 'axios';
import store from 'store';
import { UseProposalsInput } from './proposals.types';
import { useQuery } from '@tanstack/react-query';
import { proposalsKeys } from './proposals.keys';

const NO_SUPERVISOR_ID = '__NO_SUPERVISOR__';
const API_NO_SUPERVISOR_VALUE = 'null';

const getProposalList: {
  ({ sorting, columnFilters, pagination }: UseProposalsInput): Promise<any>;
} = async ({ sorting, columnFilters, pagination }: UseProposalsInput) => {
  const header = {
    Authorization: `Token ${store.getState().user.token}`,
  };

  let ordering = '';
  for (let i = 0; i < sorting.length; i++) {
    const id = sorting[i].id;
    const desc = sorting[i].desc ? '-' : '';
    ordering += `${desc}${id}`;
    if (i !== sorting.length - 1) {
      ordering += ',';
    }
  }
  ordering += ',-lastModified';

  const offset = pagination.pageIndex * pagination.pageSize;
  const limit = pagination.pageSize;

  const mapPublisherSupervisorValue = (value: unknown): string =>
    value === NO_SUPERVISOR_ID ? API_NO_SUPERVISOR_VALUE : String(value);

  let filters = {};
  filters = columnFilters.reduce((acc: { [key: string]: any }, filter) => {
    if (filter.value) {
      if (filter.id === 'assigneeStr') {
        acc['assignee'] = Array.isArray(filter.value)
          ? filter.value.join(',')
          : filter.value;
      } else if (filter.id === 'publisher_name') {
        acc['publisher_id'] = Array.isArray(filter.value)
          ? filter.value.join(',')
          : filter.value;
      } else if (filter.id === 'campaign_clientStr') {
        acc['campaign_client'] = Array.isArray(filter.value)
          ? filter.value.join(',')
          : filter.value;
      } else if (filter.id === 'campaign_name') {
        acc['campaign_name_search'] = Array.isArray(filter.value)
          ? filter.value.join(',')
          : filter.value;
      } else if (filter.id === 'generalProductValue_name') {
        acc['generalProductValue_name_search'] = Array.isArray(filter.value)
          ? filter.value.join(',')
          : filter.value;
      } else if (filter.id === 'publisherSupervisorStr') {
        acc['publisherSupervisor'] = Array.isArray(filter.value)
          ? filter.value.map(mapPublisherSupervisorValue).join(',')
          : mapPublisherSupervisorValue(filter.value);
      } else {
        // otherwise use the original id
        acc[filter.id] = Array.isArray(filter.value)
          ? filter.value.join(',')
          : filter.value;
      }
    }
    return acc;
  }, {});

  const res = await axios({
    baseURL: process.env.REACT_APP_ADSERVER_ENDPOINT + '/proposals',
    method: 'GET',
    headers: header,
    params: {
      offset,
      limit,
      ...(ordering && { ordering }),
      ...filters,
    },
  });
  return res.data;
};

export const useGetProposalList = ({
  sorting,
  columnFilters,
  pagination,
}: UseProposalsInput): {
  proposalListData: any;
  proposalListStatus: string;
  isProposalListLoading: boolean;
} => {
  const {
    data: proposalListData,
    status: proposalListStatus,
    isLoading: isProposalListLoading,
  } = useQuery<any, AxiosError>({
    queryKey: proposalsKeys.list({ sorting, columnFilters, pagination }),
    queryFn: () => getProposalList({ sorting, columnFilters, pagination }),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  return {
    proposalListData,
    proposalListStatus,
    isProposalListLoading,
  };
};
